/**
 * Utility for Vi App ↔ PWA communication via the React Native WebView bridge.
 *
 * Message contract: { type: "<ACTION_TYPE>", payload: { ... } }
 *
 * Permission protocol:
 *   Web → Native:  viPostMessage("permission", { permissionName: <key> })
 *   Native → Web:  localStorage.setItem(<key>, "granted" | "never_ask_again" | ...)
 *
 * Platform permission keys:
 *   Android: android.permission.CAMERA  (iOS does not use the bridge)
 */

/** True when running inside the Vi React Native WebView. */
export const isInWebView = () => !!window.ReactNativeWebView;

/** True when running on iOS (iPhone / iPad). */
export const isIOS = () => {
  console.log(navigator.userAgent);
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
  

/**
 * Sends a structured message to the Vi native app via the WebView bridge.
 * No-op when not running inside a WebView.
 * All outgoing messages are logged to the console for debugging.
 *
 * @param {string} type - Action type (e.g. "navigation", "permission", "file_viewer")
 * @param {object} payload - Action-specific data
 */
export const viPostMessage = (type, payload) => {
  if (!isInWebView()) return;
  const message = JSON.stringify({ type, payload });
  console.log('[RN →] postMessage', type, payload);
  window.ReactNativeWebView.postMessage(message);
};

/**
 * Returns the cached permission status from localStorage.
 * We primarily handle "granted". Any other non-null value is treated as denied.
 * @param {string} permission - e.g. "android.permission.CAMERA"
 * @returns {'granted'|'denied'|null} 'granted', 'denied', or null (never requested)
 */
export const getNativePermissionStatus = (permission) => {
  const value = localStorage.getItem(permission);
  if (value === null) return null;
  if (value === 'granted') return 'granted';
  return 'denied';
};

/**
 * Requests a native permission and waits for the result via localStorage polling.
 *
 * @param {string} permission - e.g. "android.permission.CAMERA"
 * @param {object} options
 * @param {number} options.timeoutMs - max wait time (default 10000)
 * @param {number} options.pollIntervalMs - poll interval (default 250)
 * @returns {Promise<string>} resolves with the raw localStorage value (e.g. "granted", "never_ask_again")
 * @throws {Error} if not in WebView or timeout exceeded
 */
export const requestNativePermission = (permission, options = {}) => {
  const { timeoutMs = 10000, pollIntervalMs = 250 } = options;

  return new Promise((resolve, reject) => {
    if (!isInWebView()) {
      reject(new Error('Not running inside a React Native WebView'));
      return;
    }

    // Clear stale value so we wait for a fresh native response
    localStorage.removeItem(permission);

    console.log('[RN Permission] Requesting:', permission, '— waiting for native response via localStorage…');
    viPostMessage('permission', { permissionName: permission });

    const startTime = Date.now();

    const intervalId = setInterval(() => {
      const value = localStorage.getItem(permission);

      if (value !== null) {
        clearInterval(intervalId);
        console.log('[RN Permission] Response received:', permission, '=', value);
        resolve(value);
      } else if (Date.now() - startTime >= timeoutMs) {
        clearInterval(intervalId);
        console.warn('[RN Permission] Timed out waiting for:', permission, `(${timeoutMs}ms)`);
        reject(new Error(`Permission request timed out after ${timeoutMs}ms`));
      }
    }, pollIntervalMs);
  });
};

/**
 * Sends a message to the native app to open the OS-level app settings.
 */
export const openNativeAppSettings = () => {
  viPostMessage('open_app_settings', {});
};
